import { useState, useEffect, useRef, useCallback } from "react";
import { ChevronLeftIcon, XMarkIcon } from "@heroicons/react/24/outline";
import Button from "../../../../common/Button";
import CustomInput from "../../../../common/CustomInput";
import { Transition } from "@headlessui/react";
import InterventionCreateNote from "../../../../common/tooltip/medication-review/intervention-create";
import MedicineSelect from "../../services/select/medication-select";
import {
  isFieldError,
  isValidVigilanceSelection,
} from "../../../../../helpers/input-validations";
import ConfirmationModalNew from "../../../../common/ConfirmationModalNew";
import Draggable from "react-draggable";
import { AdherenceTypes, CommonQuestionValueTypes } from "./data";
import { DPTOptions } from "../../../text/data";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { createOrUpdateIntervention } from "../../../../../redux/modules/intervention/actions";
import { ClinicalServiceTypes } from "../../../text/data";
import { InterventionStatus } from "../../../Patient-Menu/Interventions/data";
import { createPortal } from 'react-dom';
import { ResizableBox } from "react-resizable";
import { calculateResizeBounds } from "../../../../../helpers/functions";

const InterventionCreate = ({
  open,
  selectedMedication = null,
  onClose = () => { },
  onSave = (inv) => { },
  goBack = (inv) => { },
  onSkip = () => {},
  onCross = () => {},
}) => {
  const dispatch = useDispatch();
  const { id: patientId, serviceId } = useParams();
  const navigate = useNavigate();
  const { error } = useSelector((state) => state.intervention);
  const [isSaving, setIsSaving] = useState(false);
  const contentRef = useRef(null);
  const headerRef = useRef(null);
  const footerRef = useRef(null);
  const scrollRef = useRef(null);
  const DEFAULT_WIDTH = 800;
  const DEFAULT_HEIGHT = 870;
  const DEFAULT_MIN = [300, 250];
  const DEFAULT_MAX = [1400, 1000];
  const MARGIN = 20;
  const HEADER_HEIGHT = 64;
  const VIEWPORT_BUFFER = 80;
  const FOOTER_BUFFER = 24;
  const [dimensions, setDimensions] = useState({ width: 800, height: 870 });
  const [maxConstraints, setMaxConstraints] = useState(DEFAULT_MAX);
  const [minConstraints, setMinConstraints] = useState(DEFAULT_MIN);
  const { clinicalServiceObj, clinicalServiceName } = useSelector(
    (state) => state.clinicalService
  );
  const [clicked, setClicked] = useState(false);
  const interventionTypes = {
    Pharmaceutical: "PharmaceuticalOpinion",
    Prescription: "prescriptionAdaptation",
  };

  const actionPlanOptions = [
    { label: "Adapt Prescription" },
    { label: "Change/ Start/ Stop" },
    { label: "Contact Prescriber" },
    { label: "Eliminate Patient Barriers" },
    { label: "Give Immunization" },
    { label: "Initiate Drug - Disease Monitoring" },
    { label: "Initiate Drug Therapy Monitoring" },
    { label: "Initiate Monitoring for Medical Condition" },
    { label: "Provide Education" },
    { label: "Referral to Other" },
    { label: "Referral to Physician" },
    { label: "Does not apply" },
  ];

  const DOES_NOT_APPLY_ACTION_PLAN_VALUE = "Does not apply";

  // DPTOptions now imported from shared text/data

  const AdaptationOptions = [
    { value: "ChangeDose", label: "Change Dose" },
    { value: "ChangeForm", label: "Change Dose Form" },
    { value: "ChangeRegimen", label: "Change The Regimen" },
    { value: "ChangeRoute", label: "Change The Route Of Administration" },
  ];

  const [selectedMedicine, setSelectedMedicine] = useState({
    doseOptions: [],
    dose: null,
    formOptions: [],
    form: null,
    routeOptions: [],
    route: null,
  });
  const [medicationNameInputMode, setMedicationNameInputMode] = useState(false);
  const [selectedMedicineError, setSelectedMedicineError] = useState(false);
  const [openUncodedDrug, setOpenUncodedDrug] = useState(false);

  const [interventionType, setInterventionType] = useState(
    interventionTypes.Pharmaceutical
  );
  const [DTPValue, setDTPValue] = useState(null);
  const [actionPlan, setActionPlan] = useState([]);
  const [other, setOther] = useState(false);
  const [urgency, setUrgency] = useState(null);
  const [comments, setComments] = useState(null);
  const [DTPError, setDTPError] = useState(false);
  const [urgencyError, setUrgencyError] = useState(false);
  const [interventionTypeError, setInterventionTypeError] = useState(false);
  const [id, setId] = useState(null);

  useEffect(() => {
    if (selectedMedication) {
      let interventionObj = selectedMedication.intervention;
      setId(selectedMedication.interventionId);
      setInterventionType(
        interventionObj && interventionObj.interventionType
          ? interventionObj.interventionType
          : interventionTypes.Pharmaceutical
      );
      setDTPValue(
        interventionObj && interventionObj.drugTherapyProblem
          ? interventionObj.drugTherapyProblem
          : getDefaultDTPValue()
      );
      setActionPlan(
        interventionObj && interventionObj.actionPlan
          ? interventionObj.actionPlan
          : []
      );
      setUrgency(
        interventionObj && interventionObj.urgencyLevel
          ? interventionObj.urgencyLevel
          : null
      );
      setComments(
        interventionObj && interventionObj.pharmacistComments
          ? interventionObj.pharmacistComments
          : null
      );
    }
  }, [selectedMedication, open]);

  const getDefaultDTPValue = () => {
    if (selectedMedication.consumption !== AdherenceTypes.AsPrescribed)
      return "nonCompliance";
    else if (
      selectedMedication.working === CommonQuestionValueTypes.No &&
      selectedMedication.safe === CommonQuestionValueTypes.Yes
    )
      return null;
    else if (selectedMedication.working === CommonQuestionValueTypes.No)
      return "subOptimalResponse";
    else if (selectedMedication.safe === CommonQuestionValueTypes.Yes)
      return "adverseDrugReaction";
    else return "nonCompliance";
  };

  const handleChange = (e) => {
    if (e.target.checked) {
      if (e.target.name === DOES_NOT_APPLY_ACTION_PLAN_VALUE) {
        setActionPlan([DOES_NOT_APPLY_ACTION_PLAN_VALUE]);
        setOther(false);
        return;
      }

      setActionPlan([
        ...actionPlan.filter(
          (item) => item !== DOES_NOT_APPLY_ACTION_PLAN_VALUE
        ),
        e.target.name,
      ]);
    } else {
      setActionPlan(actionPlan.filter((item) => item !== e.target.name));
    }
  };

  const vigilanceFieldsToCheck = [
    {
      field: selectedMedicine,
      checkFn: isValidVigilanceSelection,
      setErrorFn: setSelectedMedicineError,
      name: "selectedMedicine",
    },
  ];

  const fieldsToCheck = [
    {
      field: DTPValue,
      checkFn: isFieldError,
      setErrorFn: setDTPError,
      alter: false,
    },
    {
      field: urgency,
      checkFn: isFieldError,
      setErrorFn: setUrgencyError,
      alter: false,
    },
    {
      field: interventionType,
      checkFn: isFieldError,
      setErrorFn: setInterventionTypeError,
      alter: false,
    },
  ];

  const resetForm = () => {
    setSelectedMedicine(null);
    setComments("");
    setDTPValue("nonCompliance");
    setActionPlan([]);
    setOther(false);
    setUrgency(null);
    setInterventionType(interventionTypes.Pharmaceutical);
    setClicked(false);
  };

  const onSaveClick = async (interventionObj) => {
    try {
      // Validate required data
      if (!patientId) {
        console.error("Patient ID is required");
        return null;
      }

      // Determine if we have an existing medication or need to create new medication details
      const hasExistingMedication = selectedMedication?.id;
      const hasNewMedication =
        selectedMedicine && Object.keys(selectedMedicine).length > 0;

      if (!hasExistingMedication && !hasNewMedication) {
        console.error(
          "Either existing medication or new medication selection is required"
        );
        return null;
      }

      // Prepare intervention data for API
      const interventionData = {
        id:
          selectedMedication?.interventionId ||
          "00000000-0000-0000-0000-000000000000", // Empty GUID for create
        patientId: patientId,
        // Only include medicationId if we have an existing medication
        // For new medications, omit it and let the backend handle it
        ...(hasExistingMedication && { medicationId: selectedMedication.id }),
        interventionType: interventionType, // Use component variable
        status: InterventionStatus.Active,
        clinicalServiceId: serviceId, // Use serviceId from URL params
        clinicalServiceType: serviceId
          ? clinicalServiceName === ClinicalServiceTypes.AllergicRhinitis
            ? "Minor Ailment"
            : "Medication Review"
          : null, // Use clinical service name from Redux
        clinicalServiceTypeCode: serviceId
          ? clinicalServiceObj?.minorAilmentTypeCode ||
          clinicalServiceObj?.medicationReviewTypeCode
          : null, // Use clinical service name as code
        interventionAssessment: {
          interventionAssessmentForm: {
            interventionDetails: {
              // Use empty GUID for medicationId in interventionDetails when using new medication
              medicationId: hasExistingMedication
                ? selectedMedication.id
                : "00000000-0000-0000-0000-000000000000",
              whoChangedMedication:
                selectedMedication?.intervention?.whoChangedMedication || null,
              whyChangedMedication:
                selectedMedication?.intervention?.whyChangedMedication || null,
              whenChangedMedication:
                selectedMedication?.intervention?.whenChangedMedication || null,
              otherChangeReason:
                selectedMedication?.intervention?.otherChangeReason || null,
              pharmacistComments:
                comments ??
                selectedMedication?.intervention?.pharmacistComments ??
                null,
              reason: selectedMedication?.intervention?.reason || null,
              reasonGenCode:
                selectedMedication?.intervention?.reasonGenCode || null,
              reasonComments:
                selectedMedication?.intervention?.reasonComments || null,
              whenChangedDate:
                selectedMedication?.intervention?.whenChangedDate || null,
              override: selectedMedication?.intervention?.override || null,
              overrideReason:
                selectedMedication?.intervention?.overrideReason || null,
              medicationName: hasExistingMedication
                ? selectedMedication?.intervention?.medicationName
                : selectedMedicine?.displayText ||
                selectedMedicine?.label ||
                null,
              dosage: hasExistingMedication
                ? selectedMedication?.intervention?.dosage
                : selectedMedicine?.dose || null,
              form: hasExistingMedication
                ? selectedMedication?.intervention?.form
                : selectedMedicine?.form || null,
              frequency: hasExistingMedication
                ? selectedMedication?.intervention?.frequency
                : selectedMedicine?.frequency || null,
              route: hasExistingMedication
                ? selectedMedication?.intervention?.route
                : selectedMedicine?.route || null,
              comments: comments, // Use component variable
              prn: selectedMedication?.intervention?.prn || null,
              prnReason: selectedMedication?.intervention?.prnReason || null,
              sig: selectedMedication?.intervention?.sig || null,
              drugTherapyProblem: DTPValue, // Use component variable
              actionPlan: actionPlan, // Use component variable
              urgencyLevel: urgency, // Use component variable
              medicationGenCode: hasExistingMedication
                ? selectedMedication?.medicationGenCode
                : selectedMedicine?.identifier ||
                selectedMedicine?.value ||
                null,
              medicationGenCodeType: hasExistingMedication
                ? selectedMedication?.medicationGenCodeType
                : selectedMedicine?.type || null,
              // Additional properties - filled for both existing and new medications
              din: hasExistingMedication
                ? selectedMedication?.din
                : selectedMedicine?.din || null,
              manufacturerCode: hasExistingMedication
                ? selectedMedication?.manufacturerCode
                : selectedMedicine?.manufacturerCode || null,
              manufacturerName: hasExistingMedication
                ? selectedMedication?.manufacturerName
                : selectedMedicine?.manufacturerName || null,
              usualQuantity: hasExistingMedication
                ? selectedMedication?.usualQuantity
                : selectedMedicine?.usualQuantity || null,
              usualDuration: hasExistingMedication
                ? selectedMedication?.usualDuration
                : selectedMedicine?.usualDuration || null,
              dispenseQuantity: hasExistingMedication
                ? selectedMedication?.dispenseQuantity
                : selectedMedicine?.dispenseQuantity || null,
              dispenseUnit: hasExistingMedication
                ? selectedMedication?.dispenseUnit
                : selectedMedicine?.dispenseUnit || null,
            },
            currentStep: 1,
            currentSubStep: 1,
            startDateTime: new Date().toISOString(),
            endDateTime: null,
          },
        },
      };

      // Call the API and get the response
      const responseData = await dispatch(
        createOrUpdateIntervention(patientId, interventionData)
      );

      // Extract the intervention ID from the response
      const interventionId = responseData?.id;

      // Return the intervention ID
      return interventionId;
    } catch (error) {
      // Keep dialog open on error - error handling is done in the action
      console.error("Failed to save intervention:", error);
      return null;
    }
  };

  const saveIntervention = async () => {
    setIsSaving(true);
    setClicked(true);
    let allFieldsValid = true;

    if (selectedMedication === null)
      vigilanceFieldsToCheck.forEach(({ field, checkFn, setErrorFn }) => {
        const hasError = !checkFn(field);
        setErrorFn(hasError);
        if (hasError) {
          allFieldsValid = false;
        }
      });

    fieldsToCheck.forEach(({ field, checkFn, setErrorFn, alter }) => {
      const hasError = alter ? !checkFn(field) : checkFn(field);
      setErrorFn(hasError);
      if (hasError) {
        allFieldsValid = false;
      }
    });

    if (!allFieldsValid) {
      setIsSaving(false);
      return false;
    }

    let interventionObj = {
      interventionType: interventionType,
      drugTherapyProblem: DTPValue,
      actionPlan: actionPlan,
      urgencyLevel: urgency,
      pharmacistComments: comments,
    };

    const interventionId = await onSaveClick(interventionObj);
    if (!interventionId) {
      setIsSaving(false);
      return false;
    }

    if (selectedMedication !== null) {
      // Keep existing callback behavior for edit/update flow.
      onSave(interventionObj, interventionId);
    }

    setIsSaving(false);
    return true;
  };

  const manageStep = async () => {
    const isSaved = await saveIntervention();
    if (isSaved) {
      onClose(false);
      resetForm();
    }
  };

  const handleCompleteNow = async () => {
    const isSaved = await saveIntervention();
    if (!isSaved) {
      return;
    }

    onSkip();
    onClose(false);
    resetForm();
    if (patientId) {
      navigate(`/patient/${patientId}/interventions`);
    }
  };

  const moveBack = () => {
    let interventionObj = {
      interventionType: interventionType,
      drugTherapyProblem: DTPValue,
      actionPlan: actionPlan,
      urgencyLevel: urgency,
      pharmacistComments: comments,
    };

    goBack(interventionObj, id);
    resetForm();
  };

  useEffect(() => {
    if (clicked) {
      fieldsToCheck.forEach(({ field, checkFn, setErrorFn, alter }) => {
        const hasError = alter ? !checkFn(field) : checkFn(field);
        setErrorFn(hasError);
      });
    }
  }, [...fieldsToCheck.map(({ field }) => field)]);

  const [container, setContainer] = useState(null);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [portalKey, setPortalKey] = useState(0);

  useEffect(() => {
    const el = document.getElementById('main-content-area');
    if (el) setContainer(el);
  }, []);

  const scrollToBottom = (behavior = 'smooth') => {
    try {
      if (scrollRef.current && typeof scrollRef.current.scrollTo === 'function') {
        scrollRef.current.scrollTo({ top: scrollRef.current.scrollHeight, behavior });
      }
    } catch (err) {}
  };

  useEffect(() => {
    if (!actionPlan || actionPlan.length === 0) return;
    const t = setTimeout(() => scrollToBottom(), 60);
    return () => clearTimeout(t);
  }, [actionPlan.length]);

  useEffect(() => {
    if (!urgency) return;
    const t = setTimeout(() => scrollToBottom(), 60);
    return () => clearTimeout(t);
  }, [urgency]);

    useEffect(() => {
      if (!open || !contentRef.current) return;
      const el = contentRef.current;
      let mounted = true;

      const measure = () => {
        const originalWidth = el.style.width;
        el.style.width = 'auto';
        const naturalWidth = el.offsetWidth;
        el.style.width = originalWidth;

        const naturalHeight = el.offsetHeight;
        const MIN_HEIGHT = Math.max(300, Math.floor(window.innerHeight * 0.25)); // allow smaller minimum on small screens
        const capHeight = Math.min(naturalHeight, Math.max(MIN_HEIGHT, window.innerHeight - VIEWPORT_BUFFER));
        if (mounted) {
          setDimensions({ width: naturalWidth, height: capHeight });
          // compute initial contentMaxHeight so footer remains inside
          try {
            const headerH = headerRef.current ? headerRef.current.offsetHeight : 0;
            const footerH = footerRef.current ? footerRef.current.offsetHeight : 0;
            const buffer = 24;
            const calcMax = Math.max(120, capHeight - headerH - footerH - buffer);
            if (scrollRef.current) scrollRef.current.style.maxHeight = `${calcMax}px`;
          } catch (err) {}
        }
      };

      const raf = window.requestAnimationFrame(measure);
      return () => {
        mounted = false;
        window.cancelAnimationFrame(raf);
      };
    }, [open]);

  // Prevent popup from growing beyond viewport (so header and footer stay visible)
  const updateConstraints = useCallback(() => {
    const maxWidth = Math.min(DEFAULT_MAX[0], window.innerWidth - 40); // 20px margin each side
    const maxHeight = Math.min(DEFAULT_MAX[1], window.innerHeight - HEADER_HEIGHT - MARGIN); // allow taller modal, small buffer from viewport
    // allow smaller minimum on narrow screens so dialog remains resizable
    const computedMinWidth = Math.min(600, Math.max(320, window.innerWidth - 200));
    const computedMinHeight = Math.max(240, Math.floor(window.innerHeight * 0.2));
    setMaxConstraints([maxWidth, maxHeight]);
    setMinConstraints([computedMinWidth, computedMinHeight]);
    setDimensions((prev) => ({
      width: Math.min(Math.max(prev.width, computedMinWidth), maxWidth),
      height: Math.min(Math.max(prev.height, computedMinHeight), maxHeight),
    }));
  }, []);

  const handleResize = useCallback((e, { size }) => {
    const margin = MARGIN;
    const headerHeight = HEADER_HEIGHT;
    const topMargin = margin + headerHeight;
    const { constrainedWidth, constrainedHeight, newPosition } = calculateResizeBounds({
      sizeWidth: size.width,
      sizeHeight: size.height,
      maxConstraints,
      currentPosition: position,
      margin,
      topMargin,
    });

    setDimensions({ width: constrainedWidth, height: constrainedHeight });

    // compute and set scrollable content max height so footer stays inside modal
    try {
      const headerH = headerRef.current ? headerRef.current.offsetHeight : 0;
      const footerH = footerRef.current ? footerRef.current.offsetHeight : 0;
      const buffer = FOOTER_BUFFER; // spacing
      const calcMax = Math.max(120, constrainedHeight - headerH - footerH - buffer);
      if (scrollRef.current) scrollRef.current.style.maxHeight = `${calcMax}px`;
    } catch (err) {
      // ignore
    }

    if (newPosition.x !== position.x || newPosition.y !== position.y) {
      setPosition(newPosition);
    }
  }, [maxConstraints, position, MARGIN, HEADER_HEIGHT, FOOTER_BUFFER]);

  useEffect(() => {
    updateConstraints();
    window.addEventListener("resize", updateConstraints);
    return () => window.removeEventListener("resize", updateConstraints);
  }, [updateConstraints]);

  // Reset size and position when the popup is closed so reopening starts fresh
  useEffect(() => {
    if (!open) {
      setDimensions({ width: 800, height: 870 });
      setPosition({ x: 0, y: 0 });
      setPortalKey((k) => k + 1);
    }
  }, [open]);

  if (!container) return null;

  return createPortal(
    <Transition
      enter="transition-opacity duration-300"
      enterFrom="opacity-0"
      enterTo="opacity-100"
      leave="transition-opacity duration-300"
      leaveFrom="opacity-100"
      leaveTo="opacity-0"
      show={open}
    >
      <div className="absolute inset-0 flex items-center justify-center z-50 pointer-events-auto">
        <Draggable
          handle=".resize-header"
          bounds="parent"
          position={position}
          onDrag={(e, data) => setPosition({ x: data.x, y: data.y })}
        >
          <ResizableBox key={portalKey}
            width={dimensions.width}
            height={dimensions.height}
            minConstraints={minConstraints}
            maxConstraints={maxConstraints}
            resizeHandles={["se"]}
            onResize={handleResize}
            draggableOpts={{ enableUserSelectHack: false }}
          >
            <div ref={contentRef} className="bg-white dialog pointer-events-auto border border-gray-300 rounded shadow-md h-full overflow-visible">
              <div className="" onClick={() => onCross()} aria-hidden="true" />
              <div className="flex flex-col flex-1 h-full min-h-0">
                <div onDoubleClick={() => setDimensions({ width: 763, height: 800 })} className="relative flex-1 resize-header flex flex-col min-h-0">
                  <div ref={headerRef} className="dialog-header z-30 flex flex-row py-4 px-6 gap-4 items-start justify-between space-x-3 bg-gray-20 border-b border-gray-100">
                    <div className="flex flex-col space-x-1">
                      {selectedMedication !== null &&
                        selectedMedication.consumption !==
                        AdherenceTypes.AsPrescribed && (
                          <button
                            className="flex items-center"
                            onClick={() => {
                              moveBack();
                            }}
                          >
                            <ChevronLeftIcon className="w-4 h-4 text-teal-300 mb-2" />
                            <h3 className="flex flex-row text-body-sm font-semibold text-teal-300 hover:border-b-2 mb-2 hover:border-teal-300">
                              Back to Assessment
                            </h3>
                          </button>
                        )}
                      <h2 className="font-bold text-gray-900 text-display-xs">
                        Create Intervention - Details
                      </h2>
                    </div>
                    <div className="flex items-center h-7">
                      <button
                        type="button"
                        className="flex items-center justify-center p-1 transition-all rounded-full max-w-8 cursor-pointer right-4 top-3 bg-blue-200 hover:bg-REELTeal hover:text-white text-teal-300"
                        onClick={() => onClose(false)}
                      >
                        <span className="sr-only">Close panel</span>
                        <XMarkIcon className="w-5 h-5" aria-hidden="true" />
                      </button>
                    </div>
                  </div>

                  <div ref={scrollRef} className="flex-1 pt-2 px-6 flex flex-col gap-2 overflow-auto pb-6 min-h-0">
                    {selectedMedication !== null && (
                      <div className="flex flex-col gap-12">
                        <div className="flex flex-col gap-4">
                          <div className="flex flex-col">
                            <div className="text-gray-800 flex flex-row text-xl px-4 py-2 mb-4 bg-blue-100 border-2 border-blue-400 rounded-md mt-2">
                              <span className="font-semibold text-body-lg text-REELTeal flex items-start">
                                Adherence Status -{" "}
                                {selectedMedication.consumption ===
                                  AdherenceTypes.Stopped
                                  ? "Stopped Taking Medication"
                                  : selectedMedication.consumption ===
                                    AdherenceTypes.NotAsPrescribed
                                    ? "Not Taking as Prescribed"
                                    : "Taking as Prescribed"}
                              </span>
                            </div>
                            <div className="flex-row text-gray-800 flex">
                              <span className="text-base font-medium items-start flex min-w-[290px]">
                                Original Prescription:
                              </span>
                              <p className="text-base font-semibold items-start flex justify-start">
                                {selectedMedication?.name}
                              </p>
                            </div>
                          </div>
                          {selectedMedication.consumption ===
                            AdherenceTypes.NotAsPrescribed && (
                              <div className="flex flex-row space-x-5">
                                <p className="flex-col min-w-[270px] flex items-start">
                                  <span className="text-base font-medium text-gray-800">
                                    Current Regimen
                                  </span>
                                </p>
                                <p className="min-w-[270px] flex flex-col items-start">
                                  <span className="text-base font-semibold text-REELTeal">
                                    {
                                      selectedMedication?.intervention
                                        ?.medicationName
                                    }{" "}
                                    {selectedMedication?.intervention?.route} [
                                    {selectedMedication?.intervention?.dosage}{" "}
                                    {selectedMedication?.intervention?.form}{" "}
                                    {selectedMedication?.intervention?.frequency}]
                                  </span>
                                </p>
                              </div>
                            )}
                        </div>
                      </div>
                    )}

                    <div className="gap-8">
                      <div className="flex flex-col gap-5">
                        <div className="flex flex-row space-x-5 mt-2">
                          {selectedMedication === null && (
                            <>
                              <MedicineSelect
                                onChange={(medicine) => {
                                  setSelectedMedicine(medicine);
                                }}
                                isRequired={true}
                                onNewCreate={(value) =>
                                  setMedicationNameInputMode(value)
                                }
                                error={selectedMedicineError}
                                value={selectedMedicine}
                              />
                              {medicationNameInputMode && (
                                <p className="col-span-12 text-base text-red-200 font-medium">
                                  This is an UNCODED DRUG - No Clinical Check
                                  will be performed
                                </p>
                              )}
                            </>
                          )}
                        </div>

                        <div className="border-t-[1px] border-gray-100"></div>
                        <div className="flex flex-row space-x-2">
                          <p className="min-w-[270px] text-gray-800 flex items-center">
                            <span className="text-base font-normal">
                              Intervention Type *
                            </span>
                          </p>
                          <div className="flex gap-10">
                            <div className="flex items-center gap-2">
                              <input
                                id="patient"
                                name="Attendee-type"
                                type="radio"
                                value={interventionTypes.Pharmaceutical}
                                checked={
                                  interventionType ===
                                  interventionTypes.Pharmaceutical
                                }
                                className="w-4 h-4  cursor-pointer  text-teal-50  focus:ring-transparent"
                                onChange={() => {
                                  setInterventionType(
                                    interventionTypes.Pharmaceutical
                                  );
                                }}
                              />
                              <label
                                className="cursor-pointer text-base font-normal whitespace-nowrap"
                                htmlFor="patient"
                              >
                                Pharmaceutical Opinion
                              </label>
                            </div>
                            <div className="flex items-center gap-2">
                              <input
                                id="patient-caregiver"
                                name="Attendee-type"
                                type="radio"
                                value={interventionTypes.Prescription}
                                checked={
                                  interventionType ===
                                  interventionTypes.Prescription
                                }
                                className="w-4 h-4  cursor-pointer  text-teal-50  focus:ring-transparent"
                                onChange={() => {
                                  setInterventionType(
                                    interventionTypes.Prescription
                                  );
                                }}
                              />
                              <label
                                className="cursor-pointer text-base font-normal whitespace-nowrap"
                                htmlFor="patient-caregiver"
                              >
                                Prescription Adaptation
                              </label>
                            </div>
                          </div>
                        </div>
                        {interventionType ===
                          interventionTypes.Pharmaceutical ? (
                          <div className="flex flex-row space-x-2">
                            <p className="min-w-[270px] text-gray-800 flex items-center">
                              <span className="text-base font-normal">
                                Drug-Therapy Problem (DTP) *
                              </span>
                            </p>
                            <CustomInput
                              options={DPTOptions}
                              name="DPTOptions"
                              className={`w-full !text-sm !font-normal ${DTPValue === null ? "!bg-blue-100" : "bg-white"
                                }`}
                              label=""
                              type="dropdown"
                              value={DTPValue}
                              onChange={(e) => setDTPValue(e.value)}
                              inputClassName="!text-sm"
                              error={DTPError}
                            />
                          </div>
                        ) : (
                          <div className="flex flex-row space-x-2">
                            <p className="min-w-[270px] text-gray-800 flex items-center">
                              <span className="text-base font-normal">
                                Select Reason for Adaptation *
                              </span>
                            </p>
                            <CustomInput
                              options={AdaptationOptions}
                              name="AdaptationOptions"
                              className="w-full bg-white !text-sm !font-normal"
                              label=""
                              type="dropdown"
                              value={DTPValue}
                              onChange={(e) => setDTPValue(e.value)}
                              inputClassName="!text-sm"
                              error={DTPError}
                            />
                          </div>
                        )}
                        <div className="flex flex-row space-x-2">
                          <p className="flex-col min-w-[270px] flex items-start">
                            <span className="text-base font-normal">
                              Action Plan (Optional)
                              <br />
                              (Select all that apply)
                            </span>
                          </p>
                          <div className="grid grid-cols-1 gap-1 min-w-[480px]">
                            {actionPlanOptions.map((item, index) => (
                              <span
                                key={index}
                                className="flex items-center text-body-md font-normal py-0"
                              >
                                <input
                                  className=""
                                  type="checkbox"
                                  id={index}
                                  name={item.label}
                                  checked={actionPlan?.includes(item.label)}
                                  onChange={handleChange}
                                />
                                <label
                                  htmlFor={index}
                                  className="ml-4 text-gray-800 text-base font-normal"
                                >
                                  {item.label}
                                </label>
                              </span>
                            ))}
                            <span className="flex items-center">
                              <input
                                className=""
                                type="checkbox"
                                checked={other}
                                onChange={(e) => {
                                  setOther(e.target.checked);
                                  if (e.target.checked) {
                                    setActionPlan(
                                      actionPlan.filter(
                                        (item) =>
                                          item !==
                                          DOES_NOT_APPLY_ACTION_PLAN_VALUE
                                      )
                                    );
                                  }
                                }}
                              />
                              <label className="ml-4 text-gray-800 text-base font-normal">
                                Other (specify)
                              </label>
                            </span>
                            {other && (
                              <CustomInput
                                name="SpecifyOther"
                                className="col-span-1 w-full"
                                label=""
                                placeholder="Specify other"
                                type="text"
                                inputClassName="!text-base"
                              />
                            )}
                          </div>
                        </div>
                        <div className="flex flex-row space-x-2">
                          <p className="min-w-[270px] text-gray-800 flex items-center">
                            <span className="text-base font-normal">
                              Urgency Level *
                            </span>
                          </p>
                          <div className="col-span-12 flex flex-row items-start">
                            <button
                              type="button"
                              className={`disabled:opacity-40 m-2 h-4 w-auto rounded-full inline-flex border-REELTeal border text-teal-300 hover:text-teal-300  hover:bg-gray-20 transition-all duration-300 items-center font-semibold justify-center px-3 py-4
                                ${urgency === "low" ? "bg-gray-20" : "bg-white"}`}
                              onClick={() => {
                                setUrgency("low");
                              }}
                            >
                              <h3
                                className={`text-body-md font-semibold text-teal-300`}
                              >
                                Low
                              </h3>
                            </button>
                            <button
                              type="button"
                              className={`disabled:opacity-40 m-2 h-4 w-auto rounded-full inline-flex border-REELTeal border text-teal-300 hover:text-teal-300  hover:bg-gray-20 transition-all duration-300 items-center font-semibold justify-center px-3 py-4
                                ${urgency === "medium" ? "bg-gray-20" : "bg-white"}`}
                              onClick={() => {
                                setUrgency("medium");
                              }}
                            >
                              <h3
                                className={`text-body-md font-semibold text-teal-300`}
                              >
                                Medium
                              </h3>
                            </button>
                            <button
                              type="button"
                              className={`disabled:opacity-40 m-2 h-4 w-auto rounded-full inline-flex border-REELTeal border text-teal-300 hover:text-teal-300  hover:bg-gray-20 transition-all duration-300 items-center font-semibold justify-center px-3 py-4
                                ${urgency === "high" ? "bg-gray-20" : "bg-white"}`}
                              onClick={() => {
                                setUrgency("high");
                              }}
                            >
                              <h3
                                className={`text-body-md font-semibold text-teal-300`}
                              >
                                High
                              </h3>
                            </button>
                          </div>
                        </div>
                        <div className="flex flex-col text-base font-normal">
                          <CustomInput
                            name="comments"
                            className="col-span-12 "
                            inputClassName="!placeholder-gray-600 min-h-[40px] max-h-[96px] resize-y whitespace-pre-wrap break-words"
                            label=""
                            placeholder="Intervention Comments (Optional)"
                            value={comments}
                            onChange={(e) => setComments(e.target.value)}
                            type="textarea"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Action buttons */}
                  <div ref={footerRef} className="bg-gray-20 z-30 py-4 border-t border-gray-100 flex-none">
                    <div className="px-6">
                      <div className="w-full flex flex-wrap items-center gap-4">
                        <Button
                          className="flex-1 min-w-[120px] max-w-[340px]"
                          text="Cancel"
                          type="primary-outline"
                          disabled={isSaving}
                          onClick={() => {
                            onClose(false);
                          }}
                        />
                        <Button
                          className="flex-1 min-w-[120px] max-w-[340px]"
                          text="Complete Now"
                          type="primary-outline"
                          toolTipId="complete-now-tooltip"
                          TooltipComponent={() => (
                            <div className="bg-white p-2 flex flex-col max-w-[300px]">
                              <p className="text-gray-800 text-sm font-normal">
                                Complete Interventions by selecting "Interventions" from the main menu.
                              </p>
                            </div>
                          )}
                          disabled={
                            urgency === null || DTPValue === null || isSaving
                          }
                          onClick={handleCompleteNow}
                        />
                        <Button
                          className="flex-none w-[min(100%,15.5rem)] sm:w-[min(100%,17rem)] !px-3 py-3 text-base leading-snug whitespace-normal"
                          text={
                            selectedMedication?.intervention?.urgencyLevel !==
                              null &&
                              selectedMedication?.intervention?.urgencyLevel !==
                              undefined
                              ? "Update & Complete Later"
                              : "Save & Complete Later"
                          }
                          type="primary"
                          disabled={urgency === null || DTPValue === null || isSaving}
                          onClick={() => {
                            if (medicationNameInputMode) {
                              setOpenUncodedDrug(true);
                            } else manageStep();
                          }}
                          iconUrl="/icons/information-light.svg"
                          toolTipId="btn-tooltip"
                          TooltipComponent={InterventionCreateNote}
                        />
                      </div>
                    </div>
                  </div>
                  <ConfirmationModalNew
                    open={openUncodedDrug}
                    title={"Uncoded Drug Alert"}
                    type={"primary"}
                    confirmText={"Continue"}
                    cancelText="Cancel & Review"
                    content={() => (
                      <div className="flex flex-col  text-gray-700">
                        <p className="text-body-sm ">
                          An uncoded DRUG has been added.
                          <br />
                          Profile Analysis can not be performed.
                        </p>
                        <p className="text-body-sm mt-4">
                          A manual clinical check will be required.
                        </p>
                      </div>
                    )}
                    onCross={() => {
                      setOpenUncodedDrug(false);
                    }}
                    handleClose={() => {
                      setOpenUncodedDrug(false);
                    }}
                    confirmAction={() => {
                      setOpenUncodedDrug(false);
                      manageStep();
                    }}
                  />
                </div>
              </div>
            </div>
          </ResizableBox>
        </Draggable>
      </div>
    </Transition>,
    container
  );
};

export default InterventionCreate;